/**
 * Client-side debug logging: never treat the browser console as private.
 *
 * Logging is enabled only when `REACT_APP_ENABLE_DEBUG_LOGS=true` at **build time**
 * (Create React App inlines `REACT_APP_*` into the bundle).
 *
 * - **Local development:** `.env.development` sets this to `true` so `npm start` shows logs.
 * - **Production / staging:** leave unset or `false`; enable only when investigating an issue,
 *   then rebuild/redeploy with `REACT_APP_ENABLE_DEBUG_LOGS=true`, and turn it off afterward.
 *   Anyone loading that build can see console output—do not log secrets or PII.
 *
 * Optional override: `.env.local` (gitignored) can set `REACT_APP_ENABLE_DEBUG_LOGS=false` to silence logs locally.
 */

const isDebugEnabled = (): boolean =>
  process.env.REACT_APP_ENABLE_DEBUG_LOGS === "true";

/** One-line, non-sensitive summary for catch blocks (no response body / stack dumps). */
export function formatSafeErrorMessage(error: unknown): string {
  if (error === null || error === undefined) {
    return "Unknown error";
  }
  if (typeof error === "string") {
    return error;
  }
  const err = error as {
    message?: string;
    response?: { status?: number; data?: { message?: string; error?: string } };
  };
  const status = err.response?.status;
  const serverMsg =
    err.response?.data?.message || err.response?.data?.error;
  if (status !== undefined) {
    return serverMsg
      ? `HTTP ${status}: ${serverMsg}`
      : `HTTP ${status}: request failed`;
  }
  return err.message || String(error);
}

export const debug = {
  log: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  },
  info: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.info(...args);
    }
  },
  error: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.error(...args);
    }
  },
};
